// ─────────────────────────────────────────────────────────────────────────────
// PR matrix — Deployment-Area × service-type pivot (spec §10 / Gerd round-2 §F).
//
// Gerd's target PR layout is a matrix: one row per Deployment Area, one column per
// service-type bucket — BSV (Basic), EEV (Early end), QD (Outdoor), QC (Indoor ≤5F),
// QE (reserved — see below), QA (Indoor 6F+), TSA Ap (Signed TSA) — plus a per-DA
// Total and a grand-total row. Pure aggregation over a computed EligiblesView: each
// PRICED row lands in its (area, service-type) cell (count + integer cents); the
// per-stream tariff already resolved the service type. OUT_OF_SCOPE / UNRESOLVED
// rows are excluded from the cells and surfaced in `unpriced` (the §4 gap stays
// visible). A row eligible on BOTH tabs contributes a line to each stream's column.
//
// QE: Gerd's layout has a distinct QE column, but `E`/`QE` bills at the QC/Indoor≤5F
// rate (decision D1, 2026-07-08 — no separate QE rate), so those rows count in the QC
// column and the QE column stays reserved-at-0 (serviceType null → nothing maps to it).
// OPEN (Gerd): keep the always-0 QE column for layout, or drop it / split E-vs-C.
// ─────────────────────────────────────────────────────────────────────────────
/** The PR matrix columns, in Gerd's §F order (Total is computed separately). */
export const PR_MATRIX_COLUMNS = [
    { key: 'BSV', label: 'BSV', serviceType: 'Basic site visit' },
    { key: 'EEV', label: 'EEV', serviceType: 'Early end visit' },
    { key: 'QD', label: 'QD', serviceType: 'Outdoor site visit' },
    { key: 'QC', label: 'QC', serviceType: 'Indoor visit — up to 5F' },
    { key: 'QE', label: 'QE', serviceType: null }, // reserved, always 0 — E/QE bills as QC (D1); see header note
    { key: 'QA', label: 'QA', serviceType: 'Indoor visit — 6F+' },
    { key: 'TSA', label: 'TSA Ap', serviceType: 'Signed TSA' },
];
const UNZONED = '(sans zone)';
const SERVICE_TYPE_TO_COLUMN = new Map();
for (const col of PR_MATRIX_COLUMNS) {
    if (col.serviceType)
        SERVICE_TYPE_TO_COLUMN.set(col.serviceType, col.key);
}
function emptyCells() {
    const cells = {};
    for (const col of PR_MATRIX_COLUMNS)
        cells[col.key] = { count: 0, cents: 0 };
    return cells;
}
const areaLabel = (r) => r.source.areaName ?? r.source.areaId ?? UNZONED;
/**
 * Build the PR matrix from a computed eligibles view. Pure — no I/O. The amounts are
 * integer cents (summed exactly); xls/PDF rendering is an adapter, not here.
 */
export function buildPrMatrix(view) {
    const byArea = new Map();
    const unpricedAreas = new Set();
    let unpricedCount = 0;
    const consider = (r) => {
        const area = areaLabel(r);
        // A priced row maps to its service-type column; everything else (OUT_OF_SCOPE,
        // UNRESOLVED, or a priced type with no matrix column) is surfaced as unpriced.
        const colKey = r.tariff.status === 'PRICED' ? SERVICE_TYPE_TO_COLUMN.get(r.tariff.serviceType) : undefined;
        if (r.tariff.status !== 'PRICED' || !colKey) {
            unpricedCount += 1;
            unpricedAreas.add(area);
            return;
        }
        let row = byArea.get(area);
        if (!row) {
            row = { deploymentArea: area, cells: emptyCells(), totalCount: 0, totalCents: 0 };
            byArea.set(area, row);
        }
        const cell = row.cells[colKey];
        cell.count += 1;
        cell.cents += r.tariff.amountCents;
        row.totalCount += 1;
        row.totalCents += r.tariff.amountCents;
    };
    for (const r of view.ssv)
        consider(r);
    for (const r of view.tsa)
        consider(r);
    const rows = [...byArea.values()].sort((a, b) => a.deploymentArea.localeCompare(b.deploymentArea));
    const totalsCells = emptyCells();
    let grandCount = 0;
    let grandCents = 0;
    for (const row of rows) {
        for (const col of PR_MATRIX_COLUMNS) {
            totalsCells[col.key].count += row.cells[col.key].count;
            totalsCells[col.key].cents += row.cells[col.key].cents;
        }
        grandCount += row.totalCount;
        grandCents += row.totalCents;
    }
    return {
        columns: [...PR_MATRIX_COLUMNS],
        rows,
        totals: { cells: totalsCells, totalCount: grandCount, totalCents: grandCents },
        unpriced: { count: unpricedCount, deploymentAreas: [...unpricedAreas].sort((a, b) => a.localeCompare(b)) },
    };
}
//# sourceMappingURL=matrix.js.map